import { ComponentType, ReactNode } from 'react';
import { IconBaseProps } from 'react-icons/lib';
import { RoleType } from '@blisspointmedia/bpm-types/dist/Users';

export interface TestCompanyParams {
  pageID: string;
  channel: string;
}

export const enum LocationType {
  Client = 'CLIENT',
  Global = 'GLOBAL'
}

export const enum NavGroupKey {
  CHANNELS = 'channels',
  DATA_SCIENTIFIC = 'data-scientific',
  INSIGHTS = 'insights',
  INTERNAL_TOOLS = 'internal-tools',
  ACTIVITY_FEED = 'activity-feed'
}

export const enum InternalToolsSectionKey {
  CLIENT_MANAGEMENT = 'Client Management',
  AUDIT = 'Audit',
  REPORTING = 'Reporting',
  OPERATIONS = 'Operations',
  MEDIA_TEAM_TOOLS = 'Media Team Tools',
  DEVELOPER_TOOLS = 'Developer Tools'
}

export interface SearchTab {
  key: string;
  prettyName: string;
}

export interface BpmLocation {
  key: string;
  prettyName: string;
  pageComponent: ComponentType;
  icon?: ReactNode;
  type: LocationType;
  // custom specified full path, if not provided, will be constructed from the keys of it and its parents
  fullPath?: string;
  roleRequired?: RoleType;
  experiment?: string;
  // Second, company-scoped experiment. When set, the entry requires BOTH
  // `experiment` and `companyExperiment` to be enabled (mirrors bpm-frontend's
  // `enableHROAS && enableCompanyHROAS`). The tinuititest company is exempt.
  companyExperiment?: string;
  // When true, `experiment`/`companyExperiment` hide only the nav entry; the route still renders.
  experimentGatesNavOnly?: boolean;
  // When true, internal users skip `experiment`; it gates external users only.
  experimentExternalOnly?: boolean;
  mediaType?: string | string[];
  // When true, `mediaType` hides only the nav entry; the route still renders (legacy never gated the route).
  mediaTypeGatesNavOnly?: boolean;
  // Prop forwarded to pageComponent, for components that render differently per channel
  // (e.g. LinearBuying is shared by the linear-buying and radio-buying entries).
  channel?: string;
  showWhenCustomDashboards?: boolean;
  showWhenPacingClientFacing?: boolean;
  // Nav-only gate, mirrors `experimentGatesNavOnly`: shown only when the company has a
  // DTC/Commerce MMM run; the route still renders regardless (DEV-25024).
  showWhenDtcMmmRun?: boolean;
  showWhenCommerceMmmRun?: boolean;
  // Unconditionally hidden from the sidebar; the route still renders (DEV-25024).
  hideFromNav?: boolean;
  // Extra page-search results for tabs inside this page; the sidebar and routes ignore them.
  searchTabs?: SearchTab[];
  testCompanyParams?: TestCompanyParams;
  redirects?: string[];
  // Aliases of a retired page: arriving through one redirects here with `retiredFrom` in the
  // navigation state, so the page can say the old one is gone.
  retiredRedirects?: string[];
}

export type TertiaryNavItem = Omit<BpmLocation, 'type'>[];
export interface TertiaryNavSection {
  sectionLabel?: InternalToolsSectionKey;
  type: LocationType;
  navs: TertiaryNavItem;
}
export interface TertiaryNavContainer extends Omit<BpmLocation, 'pageComponent' | 'type' | 'icon'> {
  tertiaryNavs: TertiaryNavSection[];
}

// Second-level nav links never render an icon, so they can't declare one.
export type SubNavLocation = Omit<BpmLocation, 'icon'>;
export type SubNavItem = SubNavLocation | TertiaryNavContainer;
export interface SubNavContainer extends Omit<BpmLocation, 'pageComponent'> {
  subNavs: SubNavItem[];
}

export type NavItem = BpmLocation | SubNavContainer;
export interface NavGroup {
  key: NavGroupKey;
  prettyName: string;
  icon: ComponentType<IconBaseProps>;
  items: NavItem[];
  isInternal?: boolean;
  isDirect?: boolean;
}

interface ExtendedLocation {
  fullPath: string;
  searchNames: string[];
}

export type FullBpmLocation = BpmLocation & ExtendedLocation;

export type FullTertiaryNavItem = FullBpmLocation;
export interface FullTertiaryNavSection extends TertiaryNavSection {
  navs: FullTertiaryNavItem[];
}
export interface FullTertiaryNavContainer extends TertiaryNavContainer {
  parentIcon: ReactNode;
  tertiaryNavs: FullTertiaryNavSection[];
}

export type FullSubNavLocation = SubNavLocation & ExtendedLocation;
export type FullSubNavItem = FullSubNavLocation | FullTertiaryNavContainer;
export interface FullSubNavContainer extends SubNavContainer {
  fullPath: string;
  searchNames: string[];
  subNavs: FullSubNavItem[];
}

export type FullNavItem = FullBpmLocation | FullSubNavContainer;
export interface FullNavGroup extends NavGroup {
  items: FullNavItem[];
}

export const INTERNAL_TOOLS_TERTIARY_KEY = 'internal-tools';

export interface BpmNavigateInput {
  type: LocationType;
  path: string;
  prettyName: string;
  keepNavOpen?: boolean;
  cleanup?: () => void;
}
